import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, Cell
} from "recharts";
import { BarChart3, TrendingUp, Users, Truck, Calendar, Download, ChevronRight, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CONTRACT_COLORS = {
  "Barakaldo": "#007934",
  "San Fernando de Henares": "#3BAD2D",
  "Barcelona": "#0891B2",
  "Santa Eulalia": "#7C3AED"
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-bold text-slate-700 mb-1.5">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color || "#007934" }} />
          <span className="text-slate-600">{p.name || p.dataKey}:</span>
          <span className="font-semibold">{p.value}{p.dataKey === "cumplimiento" ? "%" : ""}</span>
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const { authAxios } = useAuth();
  const [comparative, setComparative] = useState([]);
  const [weekly, setWeekly] = useState({ weekly_completion: [], contracts: [] });
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState("cumplimiento");
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("vz_token");
      const url = `${BACKEND}/api/reports/export?date_from=${startDate}&date_to=${endDate}&format=xlsx`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Error al exportar");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Comparativa_Valoriza_${startDate}_${endDate}.xlsx`;
      a.click();
      toast.success("Excel descargado correctamente");
    } catch (e) {
      toast.error("Error al exportar el informe");
    } finally {
      setExporting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [comp, weekly] = await Promise.all([
        authAxios.get(`/reports/comparative?start_date=${startDate}&end_date=${endDate}`),
        authAxios.get("/dashboard/charts/weekly")
      ]);
      setComparative(comp.data);
      setWeekly(weekly.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const metrics = [
    { key: "cumplimiento", label: "Cumplimiento %", unit: "%" },
    { key: "avg_staff", label: "Media Personal", unit: "" },
    { key: "avg_vehicles", label: "Media Vehículos", unit: "" },
    { key: "total_reports", label: "Partes Totales", unit: "" },
  ];

  const topServicesAll = comparative.reduce((acc, c) => {
    (c.top_services || []).forEach(([svc, count]) => {
      acc[svc] = (acc[svc] || 0) + count;
    });
    return acc;
  }, {});
  const topServicesData = Object.entries(topServicesAll).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name: name.length > 30 ? name.slice(0, 28) + "..." : name, value }));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Informes y Comparativas</h1>
          <p className="text-slate-400 text-sm mt-0.5">Análisis comparativo entre contratos</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
            <Calendar size={14} className="text-slate-400" />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="text-sm focus:outline-none bg-transparent" />
            <span className="text-slate-300">—</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="text-sm focus:outline-none bg-transparent" />
          </div>
          <button
            data-testid="apply-filter-btn"
            onClick={fetchData}
            className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-md hover:bg-[#005c27] transition-all active:scale-95"
          >
            Aplicar
          </button>
          <button
            data-testid="export-excel-btn"
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 border border-slate-200 bg-white text-slate-700 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-all active:scale-95 disabled:opacity-60"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} className="text-emerald-600" />}
            Exportar Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Metric toggle */}
          <div className="flex gap-2 flex-wrap animate-fade-in-up">
            {metrics.map(m => (
              <button
                key={m.key}
                data-testid={`metric-${m.key}`}
                onClick={() => setActiveMetric(m.key)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeMetric === m.key ? "bg-[#007934] text-white shadow-md" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Main comparison chart */}
          <div className="card-white p-6 animate-fade-in-up stagger-1">
            <h2 className="font-bold text-slate-800 mb-1">
              Comparativa: {metrics.find(m => m.key === activeMetric)?.label}
            </h2>
            <p className="text-xs text-slate-400 mb-5">{startDate} — {endDate}</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={comparative} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="contract" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} unit={metrics.find(m => m.key === activeMetric)?.unit} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey={activeMetric} name={metrics.find(m => m.key === activeMetric)?.label} radius={[6, 6, 0, 0]}>
                  {comparative.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CONTRACT_COLORS[entry.contract] || "#007934"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Contract cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 animate-fade-in-up stagger-2">
            {comparative.map(c => {
              const color = CONTRACT_COLORS[c.contract] || "#007934";
              return (
                <div key={c.contract} data-testid={`report-card-${c.contract}`} className="card-white p-5 border-l-4" style={{ borderLeftColor: color }}>
                  <h3 className="font-bold text-slate-800 text-sm truncate">{c.contract}</h3>
                  <p className="text-xs text-slate-400 mb-3">{c.city}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Cumplimiento</span>
                      <span className="font-bold" style={{ color }}>{c.cumplimiento}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${c.cumplimiento}%`, backgroundColor: color }} />
                    </div>
                    <div className="grid grid-cols-3 gap-1 pt-1">
                      {[["Partes", c.total_reports], ["Operarios", c.avg_staff], ["Vehículos", c.avg_vehicles]].map(([l, v]) => (
                        <div key={l} className="text-center">
                          <p className="text-xs font-bold text-slate-700">{typeof v === 'number' ? v.toFixed(v % 1 === 0 ? 0 : 1) : v}</p>
                          <p className="text-[10px] text-slate-400">{l}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {c.top_services?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Top servicios</p>
                      {c.top_services.slice(0, 3).map(([svc, count]) => (
                        <div key={svc} className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-600 truncate max-w-[120px]">{svc}</span>
                          <span className="font-semibold text-slate-700 ml-2">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Top services global */}
          {topServicesData.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card-white p-6 animate-fade-in-up stagger-3">
                <h2 className="font-bold text-slate-800 mb-1">Servicios más Ejecutados</h2>
                <p className="text-xs text-slate-400 mb-4">Todos los contratos combinados</p>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topServicesData} layout="vertical" barSize={12}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#64748B" }} width={160} />
                    <Tooltip />
                    <Bar dataKey="value" name="Ejecuciones" fill="#007934" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Weekly cumplimiento */}
              <div className="card-white p-6 animate-fade-in-up stagger-4">
                <h2 className="font-bold text-slate-800 mb-1">Tendencia Semanal</h2>
                <p className="text-xs text-slate-400 mb-4">Cumplimiento últimos 7 días por contrato</p>
                <div className="space-y-3">
                  {weekly.contracts?.map(c => {
                    const color = c.color || CONTRACT_COLORS[c.name] || "#007934";
                    const data = weekly.weekly_completion?.map(d => ({ day: d.day, value: d[c.name] || 0 }));
                    const avg = data?.reduce((sum, d) => sum + d.value, 0) / (data?.length || 1);
                    return (
                      <div key={c.name} className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-xs text-slate-600 w-36 truncate">{c.name}</span>
                        <div className="flex-1 flex gap-1">
                          {(data || []).map((d, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                              <div className="w-full rounded-sm" style={{ height: `${Math.max(3, (d.value / 100) * 32)}px`, backgroundColor: d.value > 0 ? color : "#F1F5F9", opacity: d.value > 0 ? 0.85 : 0.3 }} />
                              <span className="text-[9px] text-slate-400">{d.day}</span>
                            </div>
                          ))}
                        </div>
                        <span className="text-xs font-bold w-10 text-right" style={{ color }}>{avg.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
